import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthLayout } from '../components/AuthLayout'
import { Photo } from '../components/Photo'
import { Button, ButtonLink, Card, FormError, Spinner } from '../components/ui'
import { api, call, messageOf } from '../lib/api'
import { ago } from '../lib/time'

// The page a report email links to (D41). The token in the link is the only
// key: whoever runs Cat Bowl opens it from their inbox, signed in or not.
export const Route = createFileRoute('/moderate/$id')({
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({ token: typeof s.token === 'string' ? s.token : undefined }),
  component: Moderate,
})

function Moderate() {
  const { id } = Route.useParams()
  const { token = '' } = Route.useSearch()
  const reportID = Number(id)
  const qc = useQueryClient()
  const [sure, setSure] = useState(false)
  const key = ['report', reportID, token]
  const report = useQuery({
    queryKey: key,
    queryFn: () => call(api.GET('/moderation/reports/{id}', { params: { path: { id: reportID }, query: { token } } })),
    enabled: token !== '' && Number.isFinite(reportID),
    retry: false,
  })
  const takeDown = useMutation({
    mutationFn: () => call(api.POST('/moderation/reports/{id}/takedown', { params: { path: { id: reportID } }, body: { token } })),
    onSuccess: (r) => qc.setQueryData(key, r),
  })
  const removeComment = useMutation({
    mutationFn: () => call(api.POST('/moderation/reports/{id}/remove-comment', { params: { path: { id: reportID } }, body: { token } })),
    onSuccess: (r) => qc.setQueryData(key, r),
  })

  if (report.isPending && token !== '') {
    return (
      <AuthLayout title="A report">
        <Spinner />
      </AuthLayout>
    )
  }
  const r = report.data
  if (!r) {
    return (
      <AuthLayout title="That link doesn't open a report" lede="Use the link in the newest report email, and open it whole: the long part after token= is the key.">
        <ButtonLink to="/" block>Go to Cat Bowl</ButtonLink>
      </AuthLayout>
    )
  }
  const where = [r.league, r.category && `“${r.category}”`].filter(Boolean).join(', ')
  const lede = `Report ${r.id}, ${ago(r.created_at)}.`

  // A comment's report (designs/20261019-comments.md §3): the comment is
  // what's judged, the post is context, and the action removes the comment
  // alone. The commenter is not told.
  if (r.is_comment) {
    return (
      <AuthLayout title={r.resolution === 'comment_removed' ? 'Comment removed' : 'A reported comment'} lede={lede}>
        <Card>
          {r.comment != null ? (
            <blockquote className="text-[16px] leading-snug break-words">“{r.comment}”</blockquote>
          ) : (
            <p className="text-[14.5px] text-muted">
              {r.resolution === 'comment_removed' ? 'The comment is gone, for everyone.' : 'This comment is already gone: its writer deleted it, the poster removed it, or an account was deleted.'}
            </p>
          )}
        </Card>
        {r.media && <Photo media={r.media} alt="The post the comment was under" fit className="max-h-[32dvh] [&_video]:max-h-[32dvh]" />}
        <dl className="grid gap-2 text-[14.5px]">
          <Row term="Written by">{r.poster ?? 'An account that no longer exists'}</Row>
          {where && <Row term="Under a post in">{where}</Row>}
          {r.caption && <Row term="The post">{r.caption}</Row>}
          <Row term="Reported by">{r.reporter}</Row>
          <Row term="Why">{r.reason || 'They didn’t say.'}</Row>
        </dl>
        {r.comment != null && !r.resolution && (
          <div className="grid gap-2.5">
            <FormError>{removeComment.isError && messageOf(removeComment.error)}</FormError>
            {sure ? (
              <>
                <p className="text-[14.5px]">Remove it? It disappears from under the post for everyone. The post stays. This can't be undone.</p>
                <div className="flex flex-wrap gap-2.5">
                  <Button variant="danger" busy={removeComment.isPending} onClick={() => removeComment.mutate()} id="confirm-remove">
                    Yes, remove it
                  </Button>
                  <Button onClick={() => setSure(false)}>Keep it</Button>
                </div>
              </>
            ) : (
              <Button variant="danger" onClick={() => setSure(true)} className="justify-self-start" id="remove">
                Remove this comment
              </Button>
            )}
            <p className="text-[13px] text-muted">If it's fine, do nothing: it stays hidden only from the person who reported it.</p>
          </div>
        )}
      </AuthLayout>
    )
  }
  return (
    <AuthLayout title={r.resolution === 'taken_down' ? 'Taken down' : 'A reported post'} lede={lede}>
      {r.media ? (
        <Photo media={r.media} alt="The reported post" play fit className="max-h-[52dvh] [&_video]:max-h-[52dvh]" />
      ) : (
        <Card>
          <p className="text-[14.5px] text-muted">
            {r.resolution === 'taken_down' ? 'The post and its files are gone, everywhere it was posted.' : 'This post is already gone: its owner took it down, or deleted their account.'}
          </p>
        </Card>
      )}
      <dl className="grid gap-2 text-[14.5px]">
        <Row term="Posted by">{r.poster ?? 'An account that no longer exists'}</Row>
        {where && <Row term="Where">{where}</Row>}
        {r.caption && <Row term="Caption">{r.caption}</Row>}
        <Row term="Reported by">{r.reporter}</Row>
        <Row term="Why">{r.reason || 'They didn’t say.'}</Row>
      </dl>
      {r.media && !r.resolution && (
        <div className="grid gap-2.5">
          <FormError>{takeDown.isError && messageOf(takeDown.error)}</FormError>
          {sure ? (
            <>
              <p className="text-[14.5px]">Take it down? It disappears from every category it was posted to, with its votes, and its files are deleted. This can't be undone.</p>
              <div className="flex flex-wrap gap-2.5">
                <Button variant="danger" busy={takeDown.isPending} onClick={() => takeDown.mutate()} id="confirm-takedown">
                  Yes, take it down
                </Button>
                <Button onClick={() => setSure(false)}>Keep it</Button>
              </div>
            </>
          ) : (
            <Button variant="danger" onClick={() => setSure(true)} className="justify-self-start" id="takedown">
              Take this post down
            </Button>
          )}
          <p className="text-[13px] text-muted">If it's fine, do nothing: it stays hidden only from the person who reported it.</p>
        </div>
      )}
    </AuthLayout>
  )
}

function Row({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3">
      <dt className="text-muted">{term}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  )
}
